"use client";

import type { ChangeEvent } from "react";
import { useMemo, useState } from "react";
import {
  BdCalculator,
  BdChart2,
  BdWallet,
} from "solar-icon-react/bd";
import { PAYROLL_1405_CONFIG } from "@/config/payroll1405";
import {
  calculatePayroll1405,
  formatInputNumber,
  formatRialAsTomanNumber,
  formatToman,
  parseNumber,
  toEnglishDigits,
  type Payroll1405Result,
} from "@/lib/freeServices/payroll1405Calculator";
import { cn } from "@/lib";

const summaryRows = [
  {
    label: "حداقل مزد روزانه",
    value: formatToman(PAYROLL_1405_CONFIG.dailyMinimumWageRial),
  },
  {
    label: "حداقل حقوق ماهانه",
    value: formatToman(PAYROLL_1405_CONFIG.monthlyMinimumSalaryRial),
  },
  {
    label: "حق مسکن",
    value: formatToman(PAYROLL_1405_CONFIG.housingAllowanceRial),
  },
  {
    label: "بن کارگری",
    value: formatToman(PAYROLL_1405_CONFIG.groceryAllowanceRial),
  },
  {
    label: "حق تأهل",
    value: formatToman(PAYROLL_1405_CONFIG.maritalAllowanceRial),
  },
  {
    label: "پایه سنوات",
    value: `${formatToman(PAYROLL_1405_CONFIG.seniorityDailyRial)} روزانه`,
  },
  {
    label: "حق اولاد هر فرزند",
    value: formatToman(
      PAYROLL_1405_CONFIG.childAllowanceMultiplier *
        PAYROLL_1405_CONFIG.dailyMinimumWageRial,
    ),
  },
  {
    label: "نرخ بیمه کارگر",
    value: `${(PAYROLL_1405_CONFIG.workerInsuranceRate * 100).toLocaleString(
      "fa-IR",
    )}٪`,
  },
  {
    label: "معافیت مالیاتی حقوق",
    value: formatToman(PAYROLL_1405_CONFIG.monthlyTaxExemptionRial),
  },
];

const PayrollCalculator1405 = () => {
  const [monthlySalary, setMonthlySalary] = useState("");
  const [workDays, setWorkDays] = useState("30");
  const [isMarried, setIsMarried] = useState(false);
  const [childrenCount, setChildrenCount] = useState("");
  const [hasSeniority, setHasSeniority] = useState(false);
  const [overtimeHours, setOvertimeHours] = useState("");
  const [includeHousing, setIncludeHousing] = useState(true);
  const [includeGrocery, setIncludeGrocery] = useState(true);
  const [result, setResult] = useState<Payroll1405Result | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const detailRows = useMemo(() => {
    if (!result) return [];

    return [
      { label: "حقوق پایه", value: result.baseSalaryRial },
      { label: "پایه سنوات", value: result.seniorityRial },
      { label: "حق مسکن", value: result.housingRial },
      { label: "بن کارگری", value: result.groceryRial },
      { label: "حق تأهل", value: result.maritalRial },
      { label: "حق اولاد", value: result.childAllowanceRial },
      { label: "اضافه‌کاری", value: result.overtimeRial },
      { label: "حقوق ناخالص", value: result.grossRial },
      { label: "بیمه سهم کارگر", value: result.insuranceRial },
      { label: "مالیات", value: result.taxRial },
      { label: "مجموع کسورات", value: result.totalDeductionsRial },
      { label: "حقوق خالص", value: result.netRial },
    ].filter((item) => Math.round(item.value) > 0);
  }, [result]);

  const resetResult = () => {
    if (!hasCalculated) return;
    setResult(null);
    setHasCalculated(false);
  };

  const calculate = () => {
    setHasCalculated(true);
    setResult(
      calculatePayroll1405({
        monthlyBaseSalaryToman: parseNumber(monthlySalary),
        workDays: parseNumber(workDays) || 30,
        isMarried,
        childrenCount: parseNumber(childrenCount),
        hasSeniority,
        overtimeHours: parseNumber(overtimeHours),
        includeHousing,
        includeGrocery,
      }),
    );
  };

  const recalculate = () => {
    setResult(null);
    setHasCalculated(false);
  };

  const handleMoneyChange =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(formatInputNumber(event.target.value));
      resetResult();
    };

  const handlePlainNumberChange =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(toEnglishDigits(event.target.value).replace(/[^\d.]/g, ""));
      resetResult();
    };

  return (
    <div className="relative w-full rounded-2xl bg-linear-to-b from-yellow-500 to-transparent p-[2px] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl">
      <div className="grid size-full items-stretch gap-5 rounded-[18px] bg-white p-3 sm:gap-6 sm:rounded-[22px] sm:p-4 lg:grid-cols-2 lg:p-6">
        <section className="order-1 flex h-full flex-col">
          <div className="border-primary-main/15 bg-primary-main/[0.03] mb-4 flex items-center gap-3 rounded-2xl border p-3 sm:p-4">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl sm:size-12">
              <BdWallet size={30} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base leading-8 font-bold text-slate-900 sm:text-xl">
                ماشین حساب حقوق و دستمزد ۱۴۰۵
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                رایگان · آنلاین · بدون ثبت‌نام
              </p>
            </div>
          </div>

          <div className="border-primary-main/15 mb-4 rounded-2xl border p-4">
            <h3 className="font-bold text-slate-800">
              حقوق خالص خود را همین‌جا برآورد کنید
            </h3>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              حقوق پایه، روز کارکرد، مزایا، بیمه، مالیات و اضافه‌کاری را وارد
              کنید تا برآورد حقوق ناخالص و خالص سال ۱۴۰۵ نمایش داده شود.
            </p>
          </div>

          <div className="grid gap-4 min-[520px]:grid-cols-2">
            <InputBlock
              label="حقوق پایه ماهانه (تومان)"
              value={monthlySalary}
              onChange={handleMoneyChange(setMonthlySalary)}
              placeholder="مثلا 16,625,550"
            />
            <InputBlock
              label="تعداد روز کارکرد"
              value={workDays}
              onChange={handlePlainNumberChange(setWorkDays)}
              placeholder="30"
            />
            <ToggleGroup
              label="وضعیت تأهل"
              items={[
                { label: "مجرد", active: !isMarried, onClick: () => setIsMarried(false) },
                { label: "متأهل", active: isMarried, onClick: () => setIsMarried(true) },
              ]}
              onChange={resetResult}
            />
            <InputBlock
              label="تعداد فرزند واجد شرایط"
              value={childrenCount}
              onChange={handlePlainNumberChange(setChildrenCount)}
              placeholder="مثلا 1"
            />
            <ToggleGroup
              label="سابقه بیشتر از یک سال"
              items={[
                { label: "خیر", active: !hasSeniority, onClick: () => setHasSeniority(false) },
                { label: "بله", active: hasSeniority, onClick: () => setHasSeniority(true) },
              ]}
              onChange={resetResult}
            />
            <InputBlock
              label="تعداد ساعت اضافه‌کاری"
              value={overtimeHours}
              onChange={handlePlainNumberChange(setOvertimeHours)}
              placeholder="اختیاری"
            />
          </div>

          <div className="mt-4 grid gap-3 min-[520px]:grid-cols-2">
            <SwitchCard
              label="حق مسکن"
              checked={includeHousing}
              onChange={() => {
                setIncludeHousing((prev) => !prev);
                resetResult();
              }}
            />
            <SwitchCard
              label="بن کارگری"
              checked={includeGrocery}
              onChange={() => {
                setIncludeGrocery((prev) => !prev);
                resetResult();
              }}
            />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={calculate}
              className="bg-primary-main h-14 rounded-2xl text-base font-bold text-white shadow-[0_12px_28px_rgba(65,56,168,0.25)] transition hover:brightness-105"
            >
              محاسبه حقوق
            </button>
            <button
              type="button"
              onClick={recalculate}
              className="border-primary-main/20 text-primary-main h-14 rounded-2xl border bg-white text-base font-bold transition hover:bg-primary-main/[0.04]"
            >
              محاسبه مجدد
            </button>
          </div>

          <div className="bg-primary-main/[0.03] mt-5 rounded-2xl p-3 text-sm leading-7 text-slate-600 sm:mt-auto sm:p-4">
            مقادیر مزایای ماهانه در این ابزار متناسب با روز کارکرد محاسبه
            می‌شوند. مبلغ حقوق پایه را به تومان وارد کنید.
          </div>
        </section>

        <section className="border-primary-main/20 bg-primary-main/[0.02] order-2 flex h-full min-w-0 flex-col rounded-2xl border border-dashed p-3 sm:p-4">
          <div className="mb-5 flex items-start gap-3">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl">
              <BdChart2 size={26} />
            </span>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                خروجی محاسبه حقوق
              </h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                پس از محاسبه، حقوق خالص و جزئیات مزایا و کسورات را یک‌جا
                می‌بینید.
              </p>
            </div>
          </div>

          {result ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-center">
                <p className="text-sm font-bold text-emerald-700">
                  حقوق خالص دریافتی شما
                </p>
                <p className="mt-3 text-2xl font-black text-slate-950 sm:text-3xl">
                  {formatToman(result.netRial)}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
                {detailRows.map((item) => (
                  <SummaryCard
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    highlight={item.label === "حقوق خالص"}
                  />
                ))}
              </div>
            </div>
          ) : hasCalculated ? (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
              برای محاسبه، حداقل حقوق پایه ماهانه را وارد کنید.
            </div>
          ) : (
            <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm leading-7 text-slate-500">
              فرم را تکمیل کنید و دکمه محاسبه حقوق را بزنید تا نتیجه در این
              بخش نمایش داده شود.
            </div>
          )}
        </section>
      </div>

      <section className="mt-5 rounded-[18px] bg-white p-3 shadow-[0_18px_50px_rgba(15,23,42,0.06)] sm:mt-6 sm:rounded-[22px] sm:p-5">
        <div className="mb-4 flex items-center gap-3">
          <span className="bg-yellow-500/10 text-yellow-500 flex size-10 items-center justify-center rounded-2xl">
            <BdCalculator size={26} />
          </span>
          <h3 className="text-base font-bold text-slate-900 sm:text-lg">
            جدول حقوق ۱۴۰۵
          </h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {summaryRows.map((item) => (
            <div
              key={item.label}
              className="border-primary-main/10 rounded-2xl border bg-slate-50/60 p-4"
            >
              <p className="text-sm text-slate-500">{item.label}</p>
              <p className="mt-2 text-sm font-black text-slate-900 sm:text-base">
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const InputBlock = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 focus-within:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 transition">
      <input
        value={value}
        onChange={onChange}
        inputMode="numeric"
        placeholder={placeholder}
        className="size-full border-0 bg-transparent text-left text-sm font-bold text-slate-800 outline-none"
        dir="ltr"
      />
    </div>
  </div>
);

const ToggleGroup = ({
  label,
  items,
  onChange,
}: {
  label: string;
  items: { label: string; active: boolean; onClick: () => void }[];
  onChange: () => void;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 grid h-14 grid-cols-2 rounded-xl border bg-white p-1">
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          onClick={() => {
            item.onClick();
            onChange();
          }}
          className={cn(
            "rounded-lg text-sm font-bold text-slate-600 transition",
            item.active &&
              "bg-primary-main text-white shadow-[0_8px_20px_rgba(65,56,168,0.2)]",
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  </div>
);

const SwitchCard = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <button
    type="button"
    onClick={onChange}
    className="border-primary-main/10 flex h-14 items-center justify-between rounded-xl border bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-primary-main/35"
  >
    <span>{label}</span>
    <span
      className={cn(
        "relative h-7 w-12 rounded-full bg-slate-200 transition",
        checked && "bg-primary-main",
      )}
    >
      <span
        className={cn(
          "absolute top-1 right-1 size-5 rounded-full bg-white shadow transition",
          checked && "-translate-x-5",
        )}
      />
    </span>
  </button>
);

const SummaryCard = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) => (
  <div
    className={cn(
      "border-primary-main/10 rounded-xl border bg-white p-3 text-center",
      highlight && "border-emerald-100 bg-emerald-50",
    )}
  >
    <p className="text-primary-main text-sm font-bold">{label}</p>
    <p className="mt-2 text-base font-black text-slate-900 sm:text-lg">
      {formatRialAsTomanNumber(value)}
      <span className="mr-1 text-xs font-bold text-slate-500">تومان</span>
    </p>
  </div>
);

export default PayrollCalculator1405;
